'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  useBillingInfo,
  useStartTrialMutation,
  useSubscription,
  useSubscribeMutation,
  useTeamSubscription,
} from '../../hooks/queries/useBilling';
import { useWorkspaceScope } from '../../hooks/useWorkspaceScope';
import { getAuthSession } from '../../lib/authSession';
import { useUIStore } from '../../stores/uiStore';
import type { PaymentProvider } from '../../lib/api';
import { BaseModal } from '../BaseModal';
import { Spinner } from '../loading/Spinner';
import { SeatSelector } from './SeatSelector';

type SelectableProvider = Exclude<PaymentProvider, 'BETA'>;
type Step = 'plan' | 'payment';

const providerCurrency: Record<SelectableProvider, 'krw' | 'usd'> = {
  TOSS_PAYMENTS: 'krw',
  STRIPE: 'usd',
};

const isSelectableProvider = (p: PaymentProvider): p is SelectableProvider => p !== 'BETA';

const detectProvider = (
  available: SelectableProvider[],
  defaultProvider?: PaymentProvider | null,
): SelectableProvider | null => {
  if (available.length === 0) return null;
  if (defaultProvider && isSelectableProvider(defaultProvider) && available.includes(defaultProvider)) {
    return defaultProvider;
  }
  if (available.length === 1) return available[0];
  const isKorean = typeof navigator !== 'undefined' && navigator.language.startsWith('ko');
  const preferred: SelectableProvider = isKorean ? 'TOSS_PAYMENTS' : 'STRIPE';
  return available.includes(preferred) ? preferred : available[0];
};

const formatPrice = (amount: number, currency: 'krw' | 'usd') =>
  new Intl.NumberFormat(currency === 'krw' ? 'ko-KR' : 'en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: currency === 'krw' ? 0 : 2,
  }).format(amount);

export const SubscribeModal = () => {
  const t = useTranslations('billing');
  const tCommon = useTranslations('common');
  const tSub = useTranslations('billing.subscribe');
  const { token } = useWorkspaceScope();

  const open = useUIStore((s) => s.isSubscribeModalOpen);
  const target = useUIStore((s) => s.subscribeModalTarget);
  const closeModal = useUIStore((s) => s.closeSubscribeModal);
  const queryToken = open ? token : null;

  const { data: billingInfo } = useBillingInfo(queryToken);
  const { data: subscription } = useSubscription(queryToken);
  const { data: teamSubscription } = useTeamSubscription(queryToken, target.mode === 'team' ? target.teamId : null);
  const subscribeMutation = useSubscribeMutation(token);
  const startTrialMutation = useStartTrialMutation(token);

  const [period, setPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [seats, setSeats] = useState(1);
  const [step, setStep] = useState<Step>('plan');

  const billingEnabled = getAuthSession()?.member.isBetaUser ?? false;
  const selectableProviders = (billingInfo?.providers ?? []).filter(isSelectableProvider);
  const selectedProvider = useMemo(
    () => detectProvider(selectableProviders, billingInfo?.defaultProvider),
    [billingInfo?.defaultProvider, selectableProviders],
  );
  const personalPricing = billingInfo?.pricing.personal;
  const teamPricing = billingInfo?.pricing.team;
  const currency = selectedProvider ? providerCurrency[selectedProvider] : 'krw';

  const currentSubscription = target.mode === 'team' ? teamSubscription : subscription;
  const isTeamMode = target.mode === 'team';
  const trialEnabled = isTeamMode
    ? billingInfo?.trial.teamEnabled === true
    : billingInfo?.trial.personalEnabled === true;
  const isProActive =
    currentSubscription && (currentSubscription.status === 'ACTIVE' || currentSubscription.status === 'TRIALING');

  // Payment state
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeElements, setStripeElements] = useState<{
    stripe: import('@stripe/stripe-js').Stripe;
    cardElement: import('@stripe/stripe-js').StripeCardElement;
  } | null>(null);
  const stripeInitRef = useRef(false);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [tossLoading, setTossLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep('plan');
      const minSeats = teamPricing?.minSeats ?? 1;
      setSeats(target.mode === 'team' ? Math.max(minSeats, target.defaultSeats ?? minSeats) : 1);
      setError(null);
      setDone(false);
      setTossLoading(false);
      stripeInitRef.current = false;
      setStripeReady(false);
      setStripeElements(null);
    }
  }, [open, target, teamPricing?.minSeats]);

  // --- Price helpers ---
  const getMonthlyAmount = (cur: 'krw' | 'usd'): number => {
    const pricing = isTeamMode ? teamPricing : personalPricing;
    if (!pricing) return 0;
    if (period === 'yearly') {
      const yearly = isTeamMode
        ? cur === 'krw'
          ? (teamPricing?.seatYearlyKrw ?? 0)
          : (teamPricing?.seatYearlyUsd ?? 0)
        : cur === 'krw'
          ? (personalPricing?.yearlyKrw ?? 0)
          : (personalPricing?.yearlyUsd ?? 0);
      return Math.round(yearly / 12);
    }
    return isTeamMode
      ? cur === 'krw'
        ? (teamPricing?.seatMonthlyKrw ?? 0)
        : (teamPricing?.seatMonthlyUsd ?? 0)
      : cur === 'krw'
        ? (personalPricing?.monthlyKrw ?? 0)
        : (personalPricing?.monthlyUsd ?? 0);
  };

  const getSavingsPercent = (cur: 'krw' | 'usd'): number => {
    const pricing = isTeamMode ? teamPricing : personalPricing;
    if (!pricing) return 0;
    const monthly = isTeamMode
      ? cur === 'krw'
        ? (teamPricing?.seatMonthlyKrw ?? 0)
        : (teamPricing?.seatMonthlyUsd ?? 0)
      : cur === 'krw'
        ? (personalPricing?.monthlyKrw ?? 0)
        : (personalPricing?.monthlyUsd ?? 0);
    const yearly = isTeamMode
      ? cur === 'krw'
        ? (teamPricing?.seatYearlyKrw ?? 0)
        : (teamPricing?.seatYearlyUsd ?? 0)
      : cur === 'krw'
        ? (personalPricing?.yearlyKrw ?? 0)
        : (personalPricing?.yearlyUsd ?? 0);
    if (monthly <= 0) return 0;
    return Math.round((1 - yearly / (monthly * 12)) * 100);
  };

  const savingsPercent = getSavingsPercent(currency);
  const unitAmount = getMonthlyAmount(currency);
  const totalAmount = target.mode === 'team' ? unitAmount * seats : unitAmount;
  const standardMonthlyUnitAmount = isTeamMode
    ? currency === 'krw'
      ? (teamPricing?.seatMonthlyKrw ?? 0)
      : (teamPricing?.seatMonthlyUsd ?? 0)
    : currency === 'krw'
      ? (personalPricing?.monthlyKrw ?? 0)
      : (personalPricing?.monthlyUsd ?? 0);
  const standardMonthlyAmount = isTeamMode ? standardMonthlyUnitAmount * seats : standardMonthlyUnitAmount;

  // --- Actions ---
  const handleStartPro = () => {
    if (!selectedProvider) return;
    setStep('payment');

    if (selectedProvider === 'TOSS_PAYMENTS') {
      void handleTossAuth();
    }
  };

  const handleStartTrial = useCallback(async () => {
    try {
      await startTrialMutation.mutateAsync({
        plan: target.mode === 'team' ? 'TEAM_PRO' : 'PERSONAL_PRO',
        teamId: target.mode === 'team' ? target.teamId : undefined,
        seats: target.mode === 'team' ? seats : undefined,
      });
      setDone(true);
      toast.success(t('trial.successMessage'));
    } catch {
      toast.error(t('trial.error'));
    }
  }, [seats, startTrialMutation, t, target]);

  // Stripe: init on payment step
  useEffect(() => {
    if (step !== 'payment' || selectedProvider !== 'STRIPE' || stripeInitRef.current) return;
    stripeInitRef.current = true;

    const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '';
    if (!publishableKey) {
      setError('Stripe publishable key is not configured');
      return;
    }

    const init = async () => {
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(publishableKey);
      if (!stripe || !cardContainerRef.current) return;

      const elements = stripe.elements();
      const cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': { color: '#aab7c4' },
          },
        },
      });
      cardElement.mount(cardContainerRef.current);
      setStripeElements({ stripe, cardElement });
      setStripeReady(true);
    };
    void init();
  }, [step, selectedProvider]);

  const handleTossAuth = useCallback(async () => {
    const clientKey = process.env.NEXT_PUBLIC_TOSS_PAYMENTS_CLIENT_KEY ?? '';
    if (!clientKey) {
      setError('Toss Payments client key is not configured');
      return;
    }
    const successUrl = new URL(`${window.location.origin}/billing/subscribe/success`);
    successUrl.searchParams.set('provider', 'TOSS_PAYMENTS');
    successUrl.searchParams.set('period', period);
    successUrl.searchParams.set('plan', target.mode === 'team' ? 'TEAM_PRO' : 'PERSONAL_PRO');
    if (target.mode === 'team') {
      successUrl.searchParams.set('teamId', target.teamId);
      successUrl.searchParams.set('seats', String(seats));
    }

    setTossLoading(true);
    setError(null);
    try {
      const { loadTossPayments } = await import('@tosspayments/tosspayments-sdk');
      const tossPayments = await loadTossPayments(clientKey);
      const payment = tossPayments.payment({ customerKey: `customer_${Date.now()}` });
      await payment.requestBillingAuth({
        method: 'CARD',
        successUrl: successUrl.toString(),
        failUrl: `${window.location.origin}/billing/subscribe/fail?provider=TOSS_PAYMENTS`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      setTossLoading(false);
    }
  }, [period, seats, target]);

  const handleStripeSubmit = useCallback(async () => {
    if (!stripeElements) return;
    const { stripe, cardElement } = stripeElements;
    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (stripeError) {
      setError(stripeError.message ?? tSub('fail'));
      return;
    }
    try {
      await subscribeMutation.mutateAsync({
        provider: 'STRIPE',
        plan: target.mode === 'team' ? 'TEAM_PRO' : 'PERSONAL_PRO',
        period,
        teamId: target.mode === 'team' ? target.teamId : undefined,
        seats: target.mode === 'team' ? seats : undefined,
        authData: { paymentMethodId: paymentMethod.id },
      });
      setDone(true);
      toast.success(tSub('success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : tSub('fail'));
    }
  }, [stripeElements, subscribeMutation, period, seats, target, tSub]);

  const handleSuccessClose = () => {
    closeModal();
    useUIStore
      .getState()
      .openSubscriptionModal(
        target.mode === 'team'
          ? { mode: 'team', teamId: target.teamId, teamName: target.teamName }
          : { mode: 'personal' },
      );
  };

  return (
    <BaseModal isOpen={open} onClose={closeModal} variant="bottomSheet" size="2xl">
      {/* Step: Plan selection */}
      {step === 'plan' && !done && (
        <>
          <h3 className="text-lg font-bold">
            {isTeamMode ? t('page.teamTitle', { teamName: target.teamName }) : t('page.personalTitle')}
          </h3>
          <p className="text-sm text-[var(--color-fg-muted)]">
            {isTeamMode ? t('page.teamSubtitle') : t('page.personalSubtitle')}
          </p>

          {/* Period toggle — beta only */}
          {billingEnabled && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                type="button"
                className={`btn btn-sm ${period === 'yearly' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPeriod('yearly')}
              >
                {t('yearly')}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${period === 'monthly' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPeriod('monthly')}
              >
                {t('monthly')}
              </button>
            </div>
          )}

          {/* Plan cards */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {/* FREE */}
            <div className="rounded-xl border border-base-300 p-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{t('freePlan')}</span>
                {!isProActive && <span className="badge badge-outline badge-xs">{t('currentPlan')}</span>}
              </div>
              <p className="mt-1 text-xl font-bold">
                {formatPrice(0, 'krw')}
                <span className="text-xs font-normal text-[var(--color-fg-subtle)]"> / {t('perMonth')}</span>
              </p>
              <ul className="mt-3 space-y-1 text-xs text-[var(--color-fg-muted)]">
                <li>&#10003; {t('features.free.item1')}</li>
                <li>&#10003; {t('features.free.item2')}</li>
              </ul>
            </div>

            {/* PRO */}
            <div className="rounded-xl border-2 border-primary p-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{isTeamMode ? t('teamProPlan') : t('personalProPlan')}</span>
                {isProActive && <span className="badge badge-primary badge-xs">{t('currentPlan')}</span>}
              </div>
              {billingEnabled ? (
                <>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-xl font-bold text-primary">{formatPrice(totalAmount, currency)}</span>
                    <span className="text-xs text-[var(--color-fg-subtle)]">/ {t('perMonth')}</span>
                  </div>
                  {period === 'yearly' && (
                    <div className="mt-0.5 flex items-center gap-1">
                      <span className="text-xs text-[var(--color-fg-subtle)] line-through">
                        {formatPrice(standardMonthlyAmount, currency)}
                      </span>
                      {savingsPercent > 0 && (
                        <span className="badge badge-primary badge-xs">
                          {t('saveBadge', { percent: savingsPercent })}
                        </span>
                      )}
                    </div>
                  )}
                  {period === 'yearly' && (
                    <p className="text-[10px] text-[var(--color-fg-muted)]">{t('billedAnnually')}</p>
                  )}
                </>
              ) : trialEnabled ? (
                <div className="mt-1">
                  <p className="text-sm font-semibold text-primary">{t('trial.freeLabel')}</p>
                  <p className="text-xs text-[var(--color-fg-muted)]">{t('trial.description')}</p>
                </div>
              ) : (
                <p className="mt-1 text-sm font-medium text-[var(--color-fg-muted)]">
                  {isTeamMode ? t('teamBillingUnavailable') : t('personalBillingUnavailable')}
                </p>
              )}
              <ul className="mt-3 space-y-1 text-xs text-[var(--color-fg-muted)]">
                <li className="text-primary">&#10003; {t('features.pro.item1')}</li>
                <li className="text-primary">&#10003; {t('features.pro.item2')}</li>
                <li className="text-primary">&#10003; {t('features.pro.item3')}</li>
              </ul>
            </div>
          </div>

          {isTeamMode ? (
            <div className="mt-4">
              <SeatSelector
                value={seats}
                onChange={setSeats}
                min={teamPricing?.minSeats ?? 1}
                unitPrice={formatPrice(unitAmount, currency)}
                totalPrice={formatPrice(totalAmount, currency)}
              />
            </div>
          ) : null}

          {/* CTA */}
          {!isProActive && (
            <div className="modal-action">
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                {tCommon('actions.close')}
              </button>
              {trialEnabled ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={startTrialMutation.isPending}
                  onClick={handleStartTrial}
                >
                  {startTrialMutation.isPending ? t('subscribe.processing') : t('trial.startButton')}
                </button>
              ) : billingEnabled && selectedProvider ? (
                <button type="button" className="btn btn-primary" onClick={handleStartPro}>
                  {t('startPro')}
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled>
                  {isTeamMode ? t('teamBillingUnavailable') : t('personalBillingUnavailable')}
                </button>
              )}
            </div>
          )}

          {isProActive && (
            <div className="modal-action">
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                {tCommon('actions.close')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  closeModal();
                  useUIStore
                    .getState()
                    .openSubscriptionModal(
                      target.mode === 'team'
                        ? { mode: 'team', teamId: target.teamId, teamName: target.teamName }
                        : { mode: 'personal' },
                    );
                }}
              >
                {t('subscription.title')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Step: Payment */}
      {step === 'payment' && !done && !error && selectedProvider === 'TOSS_PAYMENTS' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <Spinner size="lg" />
          <p className="text-sm text-[var(--color-fg-muted)]">{tSub('processing')}</p>
          {tossLoading && <p className="text-xs text-[var(--color-fg-muted)]">Redirecting to Tosspayments...</p>}
        </div>
      )}

      {step === 'payment' && !done && !error && selectedProvider === 'STRIPE' && (
        <>
          <h3 className="text-lg font-bold">{tSub('title')}</h3>
          <div className="mt-4">
            <div ref={cardContainerRef} className="rounded-lg border border-base-300 bg-base-100 p-4" />
          </div>
          <div className="modal-action">
            <button type="button" className="btn btn-ghost" onClick={() => setStep('plan')}>
              {tSub('back')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!stripeReady || subscribeMutation.isPending}
              onClick={handleStripeSubmit}
            >
              {subscribeMutation.isPending ? (
                <>
                  <Spinner size="sm" />
                  {tSub('processing')}
                </>
              ) : (
                tSub('title')
              )}
            </button>
          </div>
        </>
      )}

      {/* Payment error */}
      {step === 'payment' && !done && error && (
        <div className="text-center py-4">
          <p className="text-error">{error}</p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setError(null);
                setStep('plan');
              }}
            >
              {tSub('back')}
            </button>
          </div>
        </div>
      )}

      {/* Success */}
      {done && (
        <div className="text-center py-4">
          <div className="text-5xl text-success">&#10003;</div>
          <p className="mt-4 text-lg font-bold">{tSub('success')}</p>
          <button type="button" className="btn btn-primary btn-sm mt-6" onClick={handleSuccessClose}>
            {t('subscription.title')}
          </button>
        </div>
      )}
    </BaseModal>
  );
};
